import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FlaskConical,
  User,
  Calendar,
  MapPin,
  Clock,
  FileText,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
  TestTube,
  X,
  Sparkles,
  Eye,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
} from 'lucide-react';
import {
  LabOrder,
  LabResult,
  getOrderById,
  getOrderStatusLabel,
  getOrderStatusColor,
  getInterpretationLabel,
  getInterpretationColor,
  getResultPDF,
} from '@/services/labService';
import { resolvePanelName } from '@/lib/labUtils';
import { LabRangeBar, LabRange } from '@/components/intellihealth/LabRangeBar';
import { toast } from '@/hooks/use-toast';

interface LabOrderDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string | null;
  onViewPatient?: (patientId: string) => void;
  onViewResults?: (patientId: string) => void;
  onAnalyzeIntelliHealth?: (patientId: string) => void;
  onDownloadRequisition?: (orderId: string) => void;
  onDownloadResult?: (resultId: string) => void;
}


/** Parse reference range string like "10.0 - 50.0" or "300-1000" into LabRange */
function parseRefRange(rangeStr: string): LabRange | null {
  if (!rangeStr) return null;
  // Match patterns: "10.0 - 50.0", "10-50", "10.0-50.0", "> 300", "< 50"
  const dashMatch = rangeStr.match(/([\d.]+)\s*[-–—]\s*([\d.]+)/);
  if (dashMatch) {
    const low = parseFloat(dashMatch[1]);
    const high = parseFloat(dashMatch[2]);
    if (!isNaN(low) && !isNaN(high) && low < high) {
      return { low, high };
    }
  }
  return null;
}

const LabOrderDetailSheet: React.FC<LabOrderDetailSheetProps> = ({
  open,
  onOpenChange,
  orderId,
  onViewPatient,
  onViewResults,
  onAnalyzeIntelliHealth,
  onDownloadResult,
  onDownloadRequisition,
}) => {
  const [order, setOrder] = useState<LabOrder | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
    } else {
      setOrder(null);
    }
  }, [open, orderId]);

  const fetchOrderDetails = async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const orderData = await getOrderById(orderId);
      setOrder(orderData);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load order details';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getAddressLines = (address?: Record<string, unknown>) => {
    if (!address) return [];
    const line1 =
      (address.first_line_address as string) ||
      (address.first_line as string) ||
      (address.line1 as string) ||
      (address.addressLine1 as string) ||
      (address.address_line1 as string) ||
      (address.street as string) ||
      (address.address as string) ||
      '';
    const line2 =
      (address.second_line_address as string) ||
      (address.second_line as string) ||
      (address.line2 as string) ||
      (address.addressLine2 as string) ||
      (address.address_line2 as string) ||
      '';
    const city =
      (address.city as string) ||
      (address.city_name as string) ||
      '';
    const state =
      (address.state as string) ||
      (address.state_code as string) ||
      (address.state_name as string) ||
      '';
    const zipRaw =
      address.zip ||
      address.zipcode ||
      address.zip_code ||
      address.postalCode ||
      address.postal_code ||
      '';
    const zip = zipRaw ? String(zipRaw) : '';
    const cityStateZip = [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '');
    return [line1, line2, cityStateZip].filter(Boolean);
  };

  const getLocationDisplay = (details?: Record<string, unknown>) => {
    if (!details) return null;
    const location = details.location as
      | string
      | { name?: string; address?: string | Record<string, unknown>; lat?: number; lng?: number }
      | undefined;
    const address = details.address as Record<string, unknown> | undefined;

    if (typeof location === 'string') {
      return { name: location, addressLines: [] as string[], coords: null };
    }

    const addressLines =
      typeof location?.address === 'string'
        ? [location.address]
        : getAddressLines(location?.address as Record<string, unknown>) ||
        getAddressLines(address as Record<string, unknown>);

    const name = location?.name;
    const coords =
      addressLines.length > 0
        ? null
        : location?.lat != null && location?.lng != null
          ? { lat: location.lat, lng: location.lng }
          : null;

    return { name, addressLines, coords };
  };

  const patientName = order?.patient
    ? `${order.patient.full_name}`
    : 'Unknown Patient';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full sm:max-w-2xl'>
        <div className='flex h-full flex-col'>
          <div className='flex items-center justify-between px-6 py-4 border-b border-slate-200'>
            <div>
              <SheetTitle className='flex items-center gap-2 text-base'>
                <FlaskConical className='h-4 w-4 text-brand-teal' />
                Lab Order Details
              </SheetTitle>
              <SheetDescription className='text-xs mt-1'>View detailed information about this lab order</SheetDescription>
            </div>
            <SheetClose asChild>
              <button className='flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-50 transition-colors'>
                <X className='h-4 w-4 text-slate-600' />
              </button>
            </SheetClose>
          </div>

          <div className='flex-1 overflow-y-auto px-6 py-4 space-y-4'>
            {loading ? (
              <div className='space-y-3'>
                <Skeleton className='h-28 w-full' />
                <Skeleton className='h-28 w-full' />
                <Skeleton className='h-28 w-full' />
              </div>
            ) : !order ? (
              <div className='py-10 text-center'>
                <AlertCircle className='mx-auto mb-3 h-10 w-10 text-slate-300' />
                <p className='text-sm text-slate-500'>Order not found</p>
              </div>
            ) : (
              <>
                {/* Status Card */}
                <Card className='border border-slate-200'>
                  <CardHeader className='pb-2.5'>
                    <CardTitle className='text-sm font-semibold text-slate-700'>Order Status</CardTitle>
                  </CardHeader>
                  <CardContent className='pt-0'>
                    <div className='flex flex-col gap-2'>
                      <Badge className={`${getOrderStatusColor(order.status)} text-[10px] px-2 py-0.5 w-fit`}>
                        {getOrderStatusLabel(order.status)}
                      </Badge>
                      <span className='text-xs text-slate-500'>
                        Created: {formatDateTime(order.created_at)}
                      </span>
                      {(order.guided_order_id || order.guidedOrder) && (
                        <span className='text-xs text-slate-500'>
                          Linked to guided order:{' '}
                          <span className='font-mono text-slate-700'>
                            {order.guidedOrder?.id ?? order.guided_order_id}
                          </span>
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Patient Information */}
                <Card className='border border-slate-200'>
                  <CardHeader className='pb-2.5'>
                    <CardTitle className='flex items-center gap-1.5 text-sm font-semibold text-slate-700'>
                      <User className='h-4 w-4' />
                      Patient Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-2.5 pt-0'>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs text-slate-500'>Name</span>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium text-slate-900'>{patientName}</span>
                        {order.patient_id && onViewPatient && (
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => onViewPatient(order.patient_id)}
                            className='h-6 w-6 p-0'
                          >
                            <User className='h-3.5 w-3.5' />
                          </Button>
                        )}
                      </div>
                    </div>
                    {order.patient?.email && (
                      <div className='flex items-center justify-between'>
                        <span className='text-xs text-slate-500'>Email</span>
                        <span className='text-xs text-slate-700'>{order.patient.email}</span>
                      </div>
                    )}
                    {order.patient?.phone && (
                      <div className='flex items-center justify-between'>
                        <span className='text-xs text-slate-500'>Phone</span>
                        <span className='text-xs text-slate-700'>{order.patient.phone}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Tests Ordered */}
                {(() => {
                  const tests: string[] = [];
                  const meta = order.provider_metadata as Record<string, unknown> | undefined;
                  const panel = order.panel as { markers?: Array<{ name?: string }>; internalTests?: Array<{ name?: string }> } | undefined;
                  if (panel?.markers?.length) {
                    panel.markers.forEach(m => m.name && tests.push(m.name));
                  } else if (panel?.internalTests?.length) {
                    panel.internalTests.forEach(t => t.name && tests.push(t.name));
                  } else if (meta?.lab_tests && Array.isArray(meta.lab_tests)) {
                    (meta.lab_tests as Array<{ name?: string; display_name?: string }>).forEach(t =>
                      tests.push(t.display_name || t.name || '')
                    );
                  } else if (meta?.lab_test && typeof meta.lab_test === 'object') {
                    const lt = meta.lab_test as { name?: string };
                    if (lt.name) tests.push(lt.name);
                  }
                  const providerTestIds = order.provider_test_ids;
                  const testCount = providerTestIds?.length || 0;
                  if (tests.length === 0 && testCount > 0) {
                    tests.push(`${testCount} test${testCount !== 1 ? 's' : ''} ordered`);
                  }
                  if (tests.length === 0) return null;
                  return (
                    <Card className='border border-slate-200'>
                      <CardHeader className='pb-2.5'>
                        <CardTitle className='flex items-center gap-1.5 text-sm font-semibold text-slate-700'>
                          <TestTube className='h-4 w-4' />
                          Tests Ordered
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='pt-0'>
                        <ul className='space-y-1.5'>
                          {tests.filter(Boolean).map((name, i) => (
                            <li key={i} className='text-sm text-slate-700 flex items-center gap-2'>
                              <span className='h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0' />
                              {name}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Laboratory Information */}
                {(() => {
                  const lab = order?.provider_metadata?.lab_test?.lab as Record<string, unknown> | undefined;
                  const apptLocation = order?.appointment_details?.location;
                  const hasLab = lab && (lab.name || lab.first_line_address || lab.city);
                  const locationDisplay = apptLocation && typeof apptLocation === 'object' && (apptLocation as { name?: string }).name;
                  if (!hasLab && !locationDisplay) return null;
                  const addressLines = lab
                    ? [
                        lab.first_line_address || lab.first_line || lab.address,
                        lab.second_line_address || lab.second_line,
                        [lab.city, lab.state, lab.zipcode || lab.zip].filter(Boolean).join(', '),
                      ].filter(Boolean)
                    : [];
                  return (
                    <Card className='border border-slate-200'>
                      <CardHeader className='pb-2.5'>
                        <CardTitle className='flex items-center gap-1.5 text-sm font-semibold text-slate-700'>
                          <FlaskConical className='h-4 w-4' />
                          Laboratory
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='pt-0 space-y-1.5'>
                        {(lab?.name as string) && (
                          <div className='font-medium text-slate-900'>{lab.name as string}</div>
                        )}
                        {locationDisplay && !lab?.name && (
                          <div className='font-medium text-slate-900'>{locationDisplay}</div>
                        )}
                        {addressLines.length > 0 && (
                          <div className='text-xs text-slate-600 space-y-0.5'>
                            {addressLines.map((line, i) => (
                              <div key={i}>{line}</div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Order Information */}
                <Card className='border border-slate-200'>
                  <CardHeader className='pb-2.5'>
                    <CardTitle className='flex items-center gap-1.5 text-sm font-semibold text-slate-700'>
                      <TestTube className='h-4 w-4' />
                      Order Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-2.5 pt-0'>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs text-slate-500'>Panel</span>
                      <span className='text-sm font-medium text-slate-900'>
                        {resolvePanelName(order)}
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs text-slate-500'>Collection Method</span>
                      <div className='flex items-center gap-1.5'>
                        {order.collection_method === 'walk_in' ? (
                          <>
                            <MapPin className='h-3.5 w-3.5 text-slate-400' />
                            <span className='text-xs text-slate-700'>Walk-in</span>
                          </>
                        ) : (
                          <>
                            <Clock className='h-3.5 w-3.5 text-slate-400' />
                            <span className='text-xs text-slate-700'>At Home</span>
                          </>
                        )}
                      </div>
                    </div>
                    {order.scheduled_at && (
                      <div className='flex items-center justify-between'>
                        <span className='text-xs text-slate-500'>Scheduled</span>
                        <span className='text-xs text-slate-700'>{formatDateTime(order.scheduled_at)}</span>
                      </div>
                    )}
                    {order.lab_center_id && (
                      <div className='flex items-center justify-between'>
                        <span className='text-xs text-slate-500'>Lab Center ID</span>
                        <span className='font-mono text-xs text-slate-700'>{order.lab_center_id}</span>
                      </div>
                    )}
                    {order.price_charged != null && (() => {
                      // price_charged is a DECIMAL(10,2) on the backend.
                      // Sequelize returns DECIMAL as a *string* by default
                      // to preserve precision — calling .toFixed on a
                      // string crashes the whole patient view. Coerce to
                      // Number at the display site (guarded against NaN).
                      const amount = Number(order.price_charged);
                      if (!Number.isFinite(amount)) return null;
                      return (
                        <div className='flex items-center justify-between'>
                          <span className='text-xs text-slate-500'>Price Charged</span>
                          <span className='text-xs font-medium text-slate-900'>${amount.toFixed(2)}</span>
                        </div>
                      );
                    })()}
                    {order.payment_status && (
                      <div className='flex items-center justify-between'>
                        <span className='text-xs text-slate-500'>Payment Status</span>
                        <Badge variant='outline' className='capitalize text-[10px] px-1.5 py-0'>
                          {order.payment_status}
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Patient Address */}
                {order.patient_address && (
                  <Card className='border border-slate-200'>
                    <CardHeader className='pb-2.5'>
                      <CardTitle className='flex items-center gap-1.5 text-sm font-semibold text-slate-700'>
                        <MapPin className='h-4 w-4' />
                        Patient Address
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='pt-0'>
                      <div className='space-y-0.5 text-xs text-slate-700'>
                        {order.patient_address.street && <p>{order.patient_address.street}</p>}
                        {order.patient_address.city && order.patient_address.state && (
                          <p>
                            {order.patient_address.city}, {order.patient_address.state}{' '}
                            {order.patient_address.zip}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Appointment Details */}
                {order.appointment_details && (
                  <Card className='border border-slate-200'>
                    <CardHeader className='pb-2.5'>
                      <CardTitle className='flex items-center gap-1.5 text-sm font-semibold text-slate-700'>
                        <Calendar className='h-4 w-4' />
                        Appointment Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-2.5 pt-0'>
                      {order.appointment_details.scheduledAt && (
                        <div className='flex items-center justify-between'>
                          <span className='text-xs text-slate-500'>Scheduled</span>
                          <span className='text-xs text-slate-700'>
                            {formatDateTime(order.appointment_details.scheduledAt)}
                          </span>
                        </div>
                      )}
                      {order.appointment_details.date && (
                        <div className='flex items-center justify-between'>
                          <span className='text-xs text-slate-500'>Date</span>
                          <span className='text-xs text-slate-700'>
                            {typeof order.appointment_details.date === 'string'
                              ? formatDateTime(order.appointment_details.date)
                              : String(order.appointment_details.date)}
                          </span>
                        </div>
                      )}
                      {order.appointment_details.time && (
                        <div className='flex items-center justify-between'>
                          <span className='text-xs text-slate-500'>Time</span>
                          <span className='text-xs text-slate-700'>
                            {typeof order.appointment_details.time === 'string'
                              ? order.appointment_details.time
                              : String(order.appointment_details.time)}
                          </span>
                        </div>
                      )}
                      {order.appointment_details.location && (
                        <div className='flex items-start justify-between gap-2'>
                          <span className='text-xs text-slate-500'>Location</span>
                          <div className='text-right text-xs'>
                            {(() => {
                              const locationDisplay = getLocationDisplay(
                                order.appointment_details as Record<string, unknown>
                              );
                              const labAddressLines = getAddressLines(
                                order.provider_metadata?.lab_test?.lab as Record<string, unknown>
                              );
                              if (!locationDisplay) return null;
                              return (
                                <>
                                  {locationDisplay.name && (
                                    <div className='font-medium text-slate-900'>{locationDisplay.name}</div>
                                  )}
                                  {(locationDisplay.addressLines.length > 0
                                    ? locationDisplay.addressLines
                                    : labAddressLines
                                  ).map(line => (
                                    <div key={line} className='text-slate-600'>
                                      {line}
                                    </div>
                                  ))}
                                  {!locationDisplay.addressLines.length &&
                                    labAddressLines.length === 0 &&
                                    locationDisplay.coords && (
                                      <div className='text-[11px] text-slate-500'>
                                        {locationDisplay.coords.lat.toFixed(6)},{' '}
                                        {locationDisplay.coords.lng.toFixed(6)}
                                      </div>
                                    )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Lab Results */}
                {order.results && order.results.length > 0 && (
                  <Card className='border border-slate-200'>
                    <CardHeader className='pb-2.5'>
                      <CardTitle className='flex items-center gap-1.5 text-sm font-semibold text-slate-700'>
                        <CheckCircle className='h-4 w-4 text-emerald-600' />
                        Lab Results
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='pt-0'>
                      {order.results.map((result: LabResult) => {
                        const rawObservations = result.observations || [];
                        const getMarkerName = (obs: Record<string, unknown>): string =>
                          (obs.loincDisplayName as string) ||
                          (obs.providerMarkerName as string) ||
                          (obs.markerName as string) ||
                          (obs.marker_name as string) ||
                          (obs.loinc_display as string) ||
                          (obs.provider_marker_name as string) ||
                          (obs.name as string) ||
                          (obs.test_name as string) ||
                          '';
                        const getValue = (obs: Record<string, unknown>): string => {
                          const v = obs.value ?? obs.result ?? obs.resultValue ?? '';
                          return v !== undefined && v !== null && v !== '' ? String(v) : '';
                        };
                        const getRefRange = (obs: Record<string, unknown>): string =>
                          (obs.referenceRange as string) ||
                          (obs.reference_range as string) ||
                          (obs.referenceInterval as string) ||
                          (obs.reference_interval as string) ||
                          '';
                        const observations = rawObservations.filter(
                          (obs: Record<string, unknown>) => getMarkerName(obs)
                        );
                        const abnormalCount = observations.filter(
                          (o: Record<string, unknown>) => (o.interpretation as string) !== 'N'
                        ).length;
                        const handleDownloadResult = async () => {
                          if (onDownloadResult) {
                            onDownloadResult(result.id);
                          } else {
                            try {
                              const blob = await getResultPDF(result.id);
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `lab-results-${result.id}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                              toast({ title: 'Downloaded', description: 'Results PDF downloaded' });
                            } catch {
                              toast({
                                title: 'Download Failed',
                                description: 'Failed to download results',
                                variant: 'destructive',
                              });
                            }
                          }
                        };
                        return (
                          <div key={result.id} className='space-y-3'>
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center gap-2 text-sm text-slate-600'>
                                <Calendar className='h-3.5 w-3.5' />
                                {result.received_at && (
                                  <span>Received: {formatDateTime(result.received_at)}</span>
                                )}
                                <span>
                                  {observations.length} markers
                                  {abnormalCount > 0 && (
                                    <span className='text-red-600 ml-1'>
                                      ({abnormalCount} abnormal)
                                    </span>
                                  )}
                                </span>
                              </div>
                              <Button
                                variant='outline'
                                size='sm'
                                className='h-7 text-xs'
                                onClick={handleDownloadResult}
                              >
                                <Download className='mr-1.5 h-3.5 w-3.5' />
                                Download PDF
                              </Button>
                            </div>
                            {observations.length > 0 ? (
                              <div className='space-y-0 divide-y divide-slate-100 border rounded-lg overflow-hidden'>
                                {observations.map((obs: Record<string, unknown>, idx: number) => {
                                  const refRangeStr = getRefRange(obs);
                                  const parsedRange = parseRefRange(refRangeStr);
                                  const numericValue = parseFloat(getValue(obs));
                                  const unitStr = String(obs.unit || obs.units || obs.unitOfMeasure || '');
                                  const canShowBar = parsedRange && !isNaN(numericValue);
                                  const interpStr = (obs.interpretation as string) ?? '';

                                  return (
                                    <div
                                      key={idx}
                                      className='py-2.5 px-3 hover:bg-slate-50/50 transition-colors'
                                    >
                                      {canShowBar ? (
                                        <LabRangeBar
                                          label={getMarkerName(obs)}
                                          value={numericValue}
                                          unit={unitStr}
                                          range={parsedRange}
                                          compact={false}
                                          palette="provider"
                                        />
                                      ) : (
                                        <div className='flex items-center justify-between'>
                                          <div className='flex-1'>
                                            <div className='font-medium text-slate-900 text-sm'>
                                              {getMarkerName(obs)}
                                            </div>
                                            {refRangeStr && (
                                              <div className='text-xs text-slate-500 mt-0.5'>
                                                Ref: {refRangeStr}
                                              </div>
                                            )}
                                          </div>
                                          <div className='flex items-center gap-3'>
                                            <div className={`font-semibold text-sm ${getInterpretationColor(interpStr)}`}>
                                              {getValue(obs)} {unitStr}
                                            </div>
                                            <Badge
                                              className={
                                                interpStr === 'N' || interpStr === 'normal'
                                                  ? 'bg-green-100 text-green-800 border-green-200 text-[10px]'
                                                  : 'bg-red-100 text-red-800 border-red-200 text-[10px]'
                                              }
                                            >
                                              {getInterpretationLabel(interpStr)}
                                            </Badge>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className='text-sm text-slate-500 py-3 text-center'>
                                No marker data available
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Actions Footer */}
          {!loading && order && (
            <div className='flex-shrink-0 border-t border-slate-200 px-6 py-4'>
              <div className='flex flex-wrap gap-2'>
                {order.requisition_url && onDownloadRequisition && (
                  <Button
                    variant='outline'
                    onClick={() => onDownloadRequisition(order.id)}
                    className='h-9 text-sm'
                  >
                    <Download className='mr-1.5 h-3.5 w-3.5' />
                    Download Requisition
                  </Button>
                )}
                {order.patient_id && onAnalyzeIntelliHealth && order.status === 'completed' && (
                  <Button
                    onClick={() => onAnalyzeIntelliHealth(order.patient_id)}
                    className='h-9 text-sm flex-1 bg-brand-teal hover:bg-brand-teal/90 text-white'
                  >
                    <Sparkles className='mr-1.5 h-3.5 w-3.5' />
                    Analyze in IntelliHealth
                  </Button>
                )}
                {order.patient_id && onViewResults && (
                  <Button
                    variant='outline'
                    onClick={() => onViewResults(order.patient_id)}
                    className='h-9 text-sm flex-1'
                  >
                    <FileText className='mr-1.5 h-3.5 w-3.5' />
                    View Results
                  </Button>
                )}
                {order.patient_id && onViewPatient && (
                  <Button
                    variant='outline'
                    onClick={() => onViewPatient(order.patient_id)}
                    className='h-9 text-sm flex-1'
                  >
                    <User className='mr-1.5 h-3.5 w-3.5' />
                    View Patient
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LabOrderDetailSheet;
